import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { ddPanelMainAccountAPI } from '../../../api/services/dd_panel_main_accounts'
import { SubAccount } from '../../../api/services/dd_panel_sub_accounts/types.ts'
import { useDebounce } from '../../../hooks/useDebounce.ts'
import Input from '../../common/forms/Input/Input.tsx'

type RecommendationPopupProps = {
  allAccounts: SubAccount[] | null
  rowEdited: SubAccount | null
}

const Recommendation = (props: RecommendationPopupProps) => {
  const { rowEdited, allAccounts } = props

  const parentRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })

  const queryClient = useQueryClient()

  const [isFocused, setIsFocused] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [email, setEmail] = useState<string>(rowEdited?.recommender_email || '')
  const [emailSelected, setEmailSelected] = useState<string | null>(
    rowEdited?.recommender_email || null
  )
  const [filteredAccs, setFilteredAccs] = useState<string[]>([])

  const prevEmailRef = useRef<string>(rowEdited?.recommender_email || '')
  const skipNextBlurRestoreRef = useRef(false)

  const debounceSearch = useDebounce(email, 400)
  const subAccountsEmail = allAccounts?.map((acc) => acc.user_email) || []

  const searchEmail = useEffectEvent(() => {
    if (!isFocused || !isTyping) {
      setFilteredAccs([])
      return
    }

    const filtered =
      !emailSelected &&
      debounceSearch.length > 0 &&
      subAccountsEmail
        .filter((acc) => acc !== rowEdited?.user_email)
        .filter((acc) => acc.includes(debounceSearch))

    if (!filtered) setFilteredAccs([])
    if (filtered) setFilteredAccs(filtered)
  })

  useEffect(() => {
    searchEmail()
  }, [debounceSearch, isFocused, isTyping, emailSelected])

  useEffect(() => {
    if (emailSelected) {
      setEmail(emailSelected)
      setFilteredAccs([])
    }
  }, [emailSelected])

  useEffect(() => {
    if (emailSelected && email !== emailSelected) {
      setEmailSelected(null)
    }
  }, [email])

  const finishInteraction = () => {
    setIsFocused(false)
    setIsTyping(false)
    setFilteredAccs([])
    inputRef.current?.blur()
  }

  const handleSave = async (
    subAccountId: number | null,
    valueOverride?: string
  ) => {
    if (!subAccountId) return

    const valueToSave = (valueOverride ?? email).trim()

    try {
      const response = await mutation.mutateAsync({
        subAccountId,
        recommenderEmail: valueToSave
      })

      if (response.status === 200) {
        toast.success('Zapisano polecającego')
        setEmailSelected(valueToSave || null)
        prevEmailRef.current = valueToSave
        finishInteraction()
      }
    } catch {
      toast.error('Nie udało się zapisać zmian')
    }
  }

  const mutation = useMutation({
    mutationFn: async ({
      subAccountId,
      recommenderEmail
    }: {
      subAccountId: number
      recommenderEmail: string
    }) => {
      return ddPanelMainAccountAPI.setRecommender(
        subAccountId,
        recommenderEmail
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subAccounts'] })
    }
  })

  useEffect(() => {
    const updatePosition = () => {
      if (!parentRef.current) return

      const rect = parentRef.current.getBoundingClientRect()

      setDropdownPos({
        top: rect.bottom - 6,
        left: rect.left,
        width: rect.width + 100
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [])

  return (
    <div ref={parentRef} className={'relative flex flex-row gap-2'}>
      <Input
        ref={inputRef}
        id="phrase"
        autoCapitalize="off"
        name="phrase"
        type="text"
        value={email}
        onFocus={() => {
          prevEmailRef.current = emailSelected || email || ''
          setEmail('')
          setEmailSelected(null)
          setIsFocused(true)
          setIsTyping(false)
          setFilteredAccs([])
        }}
        onBlur={() => {
          if (skipNextBlurRestoreRef.current) {
            skipNextBlurRestoreRef.current = false
            setIsFocused(false)
            setIsTyping(false)
            setFilteredAccs([])
            return
          }

          setEmail(prevEmailRef.current)
          setEmailSelected(prevEmailRef.current || null)
          setIsFocused(false)
          setIsTyping(false)
          setFilteredAccs([])
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setEmail(prevEmailRef.current)
            setEmailSelected(prevEmailRef.current || null)
            finishInteraction()
          }

          if (e.key === 'Enter') {
            e.preventDefault()
            rowEdited?.id && handleSave(rowEdited.id)
          }
        }}
        onChange={(e) => {
          setEmail(e.target.value)
          setIsTyping(true)
        }}
        className={'w-full mb-0'}
        inputClassName={'pl-2'}
      />

      {isFocused && filteredAccs.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width
          }}
          className={
            'w-[300px] top-10 py-4 px-2 left-0 flex-col bg-gray-100 gap-2 mt-2 z-99999 border-2 rounded'
          }
        >
          {filteredAccs.map((acc) => (
            <div
              key={acc}
              onMouseDown={(e) => {
                e.preventDefault()
                skipNextBlurRestoreRef.current = true
                setEmail(acc)
                setEmailSelected(acc)
                rowEdited?.id && handleSave(rowEdited.id, acc)
              }}
              className="w-full max-w-full cursor-pointer hover:underline overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {acc}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Recommendation
